import { createSlice } from "@reduxjs/toolkit";

/**
 * Utility function to generate a random numeric identifier.
 * This identifier consists of a string of numeric characters.
 * You can customize the character set or length as needed.
 *
 * @returns {string} A dynamically generated random ID.
 */
const generarIdAleatorio = () => {
    const caracteres = "12345678910"; // Define the character set to use for ID generation.
    let idAleatorio = ""; // Initialize the random ID as an empty string.
    for (let i = 0; i < 10; i++) {
        // Adjust the length of the ID by modifying the loop iteration count.
        idAleatorio += caracteres.charAt(Math.floor(Math.random() * caracteres.length));
    }
    return idAleatorio; // Return the generated random ID.
};

/**
 * Utility function to get today's date in "YYYY-MM-DD" format.
 * @returns {string} The current date formatted as "YYYY-MM-DD".
 */
const obtenerFechaHoy = () => {
    const hoy = new Date();
    const year = hoy.getFullYear();
    const month = String(hoy.getMonth() + 1).padStart(2, "0");
    const day = String(hoy.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};
/**
 * Redux Toolkit Slice to manage state related to event selection.
 * Provides initial state and reducers for managing selected events.
 */
export const eventoSlice = createSlice({
    name: "evento", // The slice's name, used to identify its namespace.
    initialState: {
        // The initial state of the slice.
        status: "noSelect", // Possible values: 'checking', 'not-selected', 'selected', 'completed'.
        eventosSeleccionados: [], // Array to store the selected events.
        fechaBuscar: obtenerFechaHoy(), // Date to search for events.
        hotel: 1,
    },
    reducers: {
        /**
         * Adds a new date to the list of selected events.
         * Ensures the uniqueness of the identifier by calculating a new one based on existing IDs.
         *
         * @param {Object} state - The current state of the slice.
         * @param {Object} action - The action object containing the event to add in its payload.
         */
        setAgregarFecha: (state, { payload }) => {
            // Calculate the next idSelector based on existing events.
            const nuevoIdSelector =
                state.eventosSeleccionados.length > 0 ? Math.max(...state.eventosSeleccionados.map((evento) => evento.idSelector || 0)) + 1 : 1;

            // Add the new event to the state.
            state.eventosSeleccionados.push({ ...payload, idSelector: nuevoIdSelector });
        },

        /**
         * Modifies an existing event based on its idTemporal.
         * Updates properties such as start, end, and associated resource.
         *
         * @param {Object} state - The current state of the slice.
         * @param {Object} action - The action object containing the idTemporal and updated data in its payload.
         */
        setModificarEvento: (state, { payload }) => {
            const {
                oldId,
                idTemporal,
                _id,
                resourceId,
                title,
                start,
                end,
                color,
                user,
                estadoid,
                salonid,
                hotelid,
                token,
                selectOverlap,
                temporal,
                nombreSalon,
                temporalEditar,
            } = payload;

            // Crear el nuevo evento
            const nuevoEvento = {
                idTemporal,
                _id,
                resourceId,
                title,
                start,
                end,
                color,
                user,
                estadoid,
                salonid,
                hotelid,
                token,
                selectOverlap,
                temporal,
                nombreSalon,
                temporalEditar,
            };

            // Filtrar para eliminar cualquier evento donde oldId coincida con idTemporal
            state.eventosSeleccionados = state.eventosSeleccionados.filter((evento) => evento.idTemporal !== oldId);

            // Agregar el nuevo evento al array inmutable
            state.eventosSeleccionados = [...state.eventosSeleccionados, nuevoEvento];
        },

        /**
         * Removes an event from the list of selected events.
         * Finds and deletes the event matching the provided `idTemporal` in the payload.
         *
         * @param {Object} state - The current state of the slice.
         * @param {Object} action - The action object containing the `idTemporal` of the event to delete in its payload.
         */
        setEliminarEvento: (state, { payload }) => {
            state.eventosSeleccionados = state.eventosSeleccionados.filter((evento) => evento.idTemporal !== payload.idTemporal);
        },

        /**
         * Clears all temporary events from the selected events list.
         * Resets the eventosSeleccionados array to an empty state.
         *
         * @param {Object} state - The current state of the slice.
         */
        setLimpiarTemporales: (state) => {
            state.eventosSeleccionados = [];
            state.hotel = 1;
            state.fechaBuscar = obtenerFechaHoy();
        },

        setPreload: (state, { payload }) => {
            state.fechaBuscar = payload.fechaBuscar || obtenerFechaHoy();
            state.hotel = payload.hotel || 1;
        },

        /**
         * Selects a specific event and updates its properties.
         * Updates all events, setting a unique ID for each and customizing their color.
         *
         * @param {Object} state - The current state of the slice.
         * @param {Object} action - The action object containing the idTemporal of the event to select.
         */
        setSeleccionarEvento: (state, { payload }) => {
            const { idTemporal, fechaB, hotel } = payload;

            state.fechaBuscar = fechaB; // Update the search date with the selected event's date.
            state.hotel = hotel;

            // Update events in a single iteration, assigning unique IDs and adjusting colors.
            state.eventosSeleccionados = state.eventosSeleccionados.map((evento) => {
                const ids = parseInt(generarIdAleatorio(), 10); // Generate a new unique id for each event.
                if (evento.idTemporal === idTemporal) {
                    // Update the targeted event with a specific color.
                    return {
                        ...evento,
                        color: "#349e9e", // Set the color to teal for the selected event.
                        idTemporal: ids, // Update the idTemporal with a new unique value.
                        _id: ids, // Update the event's unique identifier.
                    };
                }
                // Update the other events with a different color.
                return {
                    ...evento,
                    color: "#f4d03f", // Set the color to yellow for non-selected events.
                    idTemporal: ids, // Update the idTemporal with a new unique value.
                    _id: ids, // Update the event's unique identifier.
                };
            });
        },
    },
});

// Export action creators generated for each reducer.
export const { setAgregarFecha, setModificarEvento, setEliminarEvento, setLimpiarTemporales, setSeleccionarEvento, setPreload } = eventoSlice.actions;

// Export the reducer to integrate it into the main Redux store.
export default eventoSlice.reducer;
